import { useState, useEffect, useCallback, useRef } from 'react';
import { navLinks, navServiceItems } from '@/mocks/homeData';

interface NavbarProps {
  onBookCleanup?: () => void;
}

export default function Navbar({ onBookCleanup = () => {} }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollYRef = useRef(0);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // iOS-safe body scroll lock
  useEffect(() => {
    if (menuOpen) {
      scrollYRef.current = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollYRef.current}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      document.body.style.width = '100%';
    } else {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollYRef.current);
    }
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      document.body.style.width = '';
    };
  }, [menuOpen]);

  const openMenu = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setClosing(false);
    setMenuOpen(true);
    setMobileDropdownOpen(false);
  };

  const closeMenu = () => {
    if (closeTimerRef.current) return;
    setClosing(true);
    closeTimerRef.current = setTimeout(() => {
      setMenuOpen(false);
      setClosing(false);
      setMobileDropdownOpen(false);
      closeTimerRef.current = null;
    }, 280);
  };

  const scrollToSection = useCallback((href: string) => {
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
    setDropdownOpen(false);
    closeMenu();
  }, []);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-[999] bg-background-50/95 backdrop-blur-md border-b border-background-200/70 transition-shadow duration-300 transform-gpu will-change-transform"
      style={{ boxShadow: scrolled ? '0 1px 12px rgba(0,0,0,0.08)' : 'none' }}
    >
      <nav className="container-content flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <a
          href="#home"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection('#home');
          }}
          className="flex items-center gap-2.5 group shrink-0"
        >
          <img
            src="/cleanup_icon.png"
            alt="CleanUp Naija"
            className="w-10 h-10 md:w-11 md:h-11 object-contain transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3"
            onError={(e) => {
              const target = e.currentTarget;
              target.style.display = 'none';
              target.parentElement!.querySelector('.logo-fallback')!.classList.remove('hidden');
            }}
          />
          <div className="logo-fallback hidden w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-xl bg-primary-500 text-background-50 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
            <i className="ri-sparkling-2-line text-xl md:text-2xl" />
          </div>
          <span className="font-heading font-medium text-[15px] md:text-lg text-foreground-950 whitespace-nowrap">
            CleanUp <span className="text-primary-600 font-semibold">Naija</span>
          </span>
        </a>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => (
            <div key={link.label} className="relative">
              {link.hasDropdown ? (
                <div
                  onMouseEnter={() => setDropdownOpen(true)}
                  onMouseLeave={() => setDropdownOpen(false)}
                >
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors duration-200 py-2"
                  >
                    {link.label}
                    <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {dropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 w-56 bg-background-50 border border-background-200 rounded-lg py-2 shadow-lg">
                      {navServiceItems.map((item) => (
                        <button
                          key={item.label}
                          onClick={() => scrollToSection(item.href)}
                          className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <a
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(link.href);
                  }}
                  className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors duration-200 py-2"
                >
                  {link.label}
                </a>
              )}
            </div>
          ))}
          <button
            onClick={onBookCleanup}
            className="bg-primary-500 text-background-50 hover:bg-primary-600 active:bg-primary-700 px-5 py-2 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap"
          >
            Book a Cleanup
          </button>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden w-12 h-12 flex items-center justify-center rounded-lg active:bg-background-100 transition-colors"
          onClick={openMenu}
          aria-label="Open menu"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <i className="ri-menu-3-line text-[22px] text-foreground-950" />
        </button>
      </nav>

      {/* Mobile side panel + overlay */}
      {menuOpen && (
        <>
          {/* Backdrop overlay */}
          <div
            className={` h-screen inset-0 z-[998] bg-black/50 md:hidden ${closing ? 'animate-overlay-out' : 'animate-overlay-in'}`}
            onClick={closeMenu}
            aria-hidden="true"
          />

          {/* Side panel */}
          <div
            className={`fixed top-0 right-0 bottom-0 z-[999] w-[300px] max-w-[85vw] bg-background-50 md:hidden flex flex-col shadow-2xl ${closing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
          >
            {/* Panel header */}
            <div className="flex items-center justify-between px-5 h-16 border-b border-background-200/70 shrink-0">
              <span className="font-heading font-semibold text-base text-foreground-950">
                Menu
              </span>
              <button
                onClick={closeMenu}
                className="w-12 h-12 flex items-center justify-center rounded-lg active:bg-background-100 transition-colors"
                aria-label="Close menu"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <i className="ri-close-large-line text-[22px] text-foreground-950" />
              </button>
            </div>

            {/* Panel body — links rendered plainly, no stagger opacity hacks */}
            <div className="flex-1 bg-white h-screen overflow-y-auto px-5 py-6">
              <div className="flex flex-col">
                {navLinks.map((link) => (
                  <div key={link.label}>
                    {link.hasDropdown ? (
                      <>
                        <button
                          onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
                          className="w-full flex items-center justify-between text-sm font-semibold text-foreground-800 py-3.5 border-b border-background-100 hover:text-primary-600 transition-colors"
                        >
                          {link.label}
                          <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${mobileDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {mobileDropdownOpen && (
                          <div className="pl-3 pb-2 space-y-0.5">
                            {navServiceItems.map((item) => (
                              <button
                                key={item.label}
                                onClick={() => scrollToSection(item.href)}
                                className="block w-full text-left py-2.5 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                              >
                                {item.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <a
                        href={link.href}
                        onClick={(e) => {
                          e.preventDefault();
                          scrollToSection(link.href);
                        }}
                        className="block text-sm font-semibold text-foreground-800 py-3.5 border-b border-background-100 hover:text-primary-600 transition-colors"
                      >
                        {link.label}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Panel footer */}
            <div className="px-5 py-5 border-t border-background-200/70 shrink-0">
              <button
                onClick={() => {
                  onBookCleanup();
                  closeMenu();
                }}
                className="w-full bg-primary-500 text-background-50 hover:bg-primary-600 active:bg-primary-700 px-5 py-3.5 rounded-md text-sm font-semibold transition-colors duration-200"
              >
                Book a Cleanup
              </button>
            </div>
          </div>
        </>
      )}
    </header>
  );
}